import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import apiClient from '../../utils/api';
import FrequencyModal from './FrequencyModal';
import AdminUserManagement from './AdminUserManagement';
import GeneralAudioManager from './GeneralAudioManager';
import ProxyCardsAdmin from './ProxyCardsAdmin';
import RecommendationsAdmin from './RecommendationsAdmin';
import ChatbotSettings from './ChatbotSettings';
import AdminAffiliatePanel from './AdminAffiliatePanel';
import EmailSettings from './EmailSettings';
import PaymentSettings from './PaymentSettings';
import OrderDashboard from './OrderDashboard';
import SubscriptionManagement from './SubscriptionManagement';
import OnetimeProductsAdmin from './OnetimeProductsAdmin';
import CoinsManagement from './CoinsManagement';
import AdminCreditHistory from './AdminCreditHistory';
import AppHeader from '../Header/AppHeader';
import './AdminDashboard.css';

function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('users');
  const [activeSubTab, setActiveSubTab] = useState('statistics'); // For users submenu
  const [frequencies, setFrequencies] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [subscriptionSettings, setSubscriptionSettings] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingFrequency, setEditingFrequency] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      if (activeTab === 'users' && activeSubTab === 'frequencies') {
        const data = await apiClient.getFrequencies();
        setFrequencies(data);
      } else if (activeTab === 'users' && activeSubTab === 'statistics') {
        const data = await apiClient.getAllUsers();
        const statsData = await apiClient.getAdminStats();
        setUsers(data);
        setStats(statsData);
      } else if (activeTab === 'users' && activeSubTab === 'subscription') {
        const [settingsData, transData] = await Promise.all([
          apiClient.getSubscriptionSettings(),
          apiClient.getAllTransactions()
        ]);
        setSubscriptionSettings(settingsData);
        setTransactions(transData);
      } else if (activeTab === 'recommendations') {
        const data = await apiClient.getAllRecommendations();
        setRecommendations(data);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }, [activeTab, activeSubTab]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    loadSubscriptionStatus();
  }, []);

  const loadSubscriptionStatus = async () => {
    try {
      const data = await apiClient.getSubscriptionStatus();
      setSubscriptionStatus(data);
    } catch (error) {
      console.error('Error loading subscription status:', error);
    }
  };


  const handleCreate = () => {
    setEditingFrequency(null);
    setShowModal(true);
  };

  const handleEdit = (frequency) => {
    setEditingFrequency(frequency);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    try {
      await apiClient.deleteFrequency(id);
      setFrequencies(frequencies.filter((f) => f.id !== id));
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Error deleting frequency:', error);
      alert('Fehler beim Löschen der Frequenz');
    }
  };

  const handleSave = async (data) => {
    try {
      if (editingFrequency) {
        // Update
        const updated = await apiClient.updateFrequency(editingFrequency.id, data);
        setFrequencies(
          frequencies.map((f) => (f.id === updated.id ? updated : f))
        );
      } else {
        // Create
        const created = await apiClient.createFrequency(data);
        setFrequencies([...frequencies, created]);
      }
      setShowModal(false);
      setEditingFrequency(null);
    } catch (error) {
      console.error('Error saving frequency:', error);
      throw error;
    }
  };

  const handleLoginAsUser = async (userId) => {
    try {
      const result = await apiClient.loginAsUser(userId);
      
      // Save admin token first
      localStorage.setItem('admin_token', localStorage.getItem('token'));
      localStorage.setItem('admin_user', JSON.stringify(user));
      
      // Set user token
      apiClient.setToken(result.token);
      
      // Reload page to refresh with user context
      window.location.href = '/dashboard';
    } catch (error) {
      console.error('Error logging in as user:', error);
      alert('Fehler beim Einloggen als User');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleGoToUser = () => {
    navigate('/dashboard');
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Lade Admin-Panel...</p>
      </div>
    );
  }

  return (
    <>
      <AppHeader subscriptionStatus={subscriptionStatus} />
      <div className="dashboard-container admin-dashboard">
        <main className="dashboard-main admin-main">
        {/* Main Navigation Tabs */}
        <div className="admin-tabs">
          <button
            className={`admin-tab ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
            data-testid="users-tab"
          >
            👥 Users
          </button>
          <button
            className={`admin-tab ${activeTab === 'recommendations' ? 'active' : ''}`}
            onClick={() => setActiveTab('recommendations')}
            data-testid="recommendations-tab"
          >
            💡 Empfehlungen
          </button>
          <button
            className={`admin-tab ${activeTab === 'chatbot' ? 'active' : ''}`}
            onClick={() => setActiveTab('chatbot')}
            data-testid="chatbot-tab"
          >
            🤖 AI-Begleiter
          </button>
          <button
            className={`admin-tab ${activeTab === 'affiliates' ? 'active' : ''}`}
            onClick={() => setActiveTab('affiliates')}
            data-testid="affiliates-tab"
          >
            💰 Affiliates
          </button>
          <button
            className={`admin-tab ${activeTab === 'orders' ? 'active' : ''}`}
            onClick={() => setActiveTab('orders')}
            data-testid="orders-tab"
          >
            📦 Bestellungen
          </button>
          <button
            className={`admin-tab ${activeTab === 'payments' ? 'active' : ''}`}
            onClick={() => setActiveTab('payments')}
            data-testid="payments-tab"
          >
            💳 Zahlungen
          </button>
          <button
            className={`admin-tab ${activeTab === 'subscriptions' ? 'active' : ''}`}
            onClick={() => setActiveTab('subscriptions')}
            data-testid="subscriptions-tab"
          >
            📋 Abonnements
          </button>
          <button
            className={`admin-tab ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
            data-testid="settings-tab"
          >
            ⚙️ Einstellungen
          </button>
        </div>

        {/* Users Sub-Navigation */}
        {activeTab === 'users' && (
          <div className="admin-subtabs">
            <button
              className={`admin-subtab ${activeSubTab === 'statistics' ? 'active' : ''}`}
              onClick={() => setActiveSubTab('statistics')}
              data-testid="statistics-subtab"
            >
              📊 Statistiken
            </button>
            <button
              className={`admin-subtab ${activeSubTab === 'frequencies' ? 'active' : ''}`}
              onClick={() => setActiveSubTab('frequencies')}
              data-testid="frequencies-subtab"
            >
              🌀 Frequenzen
            </button>
            <button
              className={`admin-subtab ${activeSubTab === 'subscription' ? 'active' : ''}`}
              onClick={() => setActiveSubTab('subscription')}
              data-testid="subscription-subtab"
            >
              💳 Abopreise
            </button>
            <button
              className={`admin-subtab ${activeSubTab === 'onetime' ? 'active' : ''}`}
              onClick={() => setActiveSubTab('onetime')}
              data-testid="onetime-subtab"
            >
              💰 Einmalpreise
            </button>
            <button
              className={`admin-subtab ${activeSubTab === 'coins' ? 'active' : ''}`}
              onClick={() => setActiveSubTab('coins')}
              data-testid="coins-subtab"
            >
              🪙 Coins
            </button>
            <button
              className={`admin-subtab ${activeSubTab === 'credit-history' ? 'active' : ''}`}
              onClick={() => setActiveSubTab('credit-history')}
              data-testid="credit-history-subtab"
            >
              📊 Credit-Verlauf
            </button>
          </div>
        )}

        {activeTab === 'orders' && <OrderDashboard />}

        {activeTab === 'payments' && <PaymentSettings />}

        {activeTab === 'subscriptions' && <SubscriptionManagement />}

        {activeTab === 'settings' && <EmailSettings />}

        {activeTab === 'affiliates' && <AdminAffiliatePanel />}

        {/* Users Tab Content - Based on SubTab */}
        {activeTab === 'users' && activeSubTab === 'frequencies' && (
          <>
            <div className="admin-header">
              <h2 className="admin-title">Frequenz-Verwaltung</h2>
              <button
                onClick={handleCreate}
                className="btn-create"
                data-testid="create-frequency-button"
              >
                + Neue Frequenz
              </button>
            </div>

        {frequencies.length === 0 ? (
          <div className="empty-state">
            <p>Keine Frequenzen vorhanden</p>
            <p className="empty-state-hint">
              Erstellen Sie Ihre erste Frequenz
            </p>
          </div>
        ) : (
          <div className="frequencies-table">
            <table>
              <thead>
                <tr>
                  <th>Icon</th>
                  <th>Name</th>
                  <th>Farbe</th>
                  <th>Beschreibung</th>
                  <th>Audio</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {frequencies.map((freq) => (
                  <tr key={freq.id} data-testid={`frequency-row-${freq.id}`}>
                    <td className="table-icon">{freq.icon}</td>
                    <td className="table-name">{freq.name}</td>
                    <td className="table-color">
                      <div className="color-preview-container">
                        <div
                          className="color-preview"
                          style={{ backgroundColor: freq.color_hex }}
                        ></div>
                        <span>{freq.color_hex}</span>
                      </div>
                    </td>
                    <td className="table-description">{freq.description}</td>
                    <td className="table-audio">
                      {freq.audio_files && freq.audio_files.length > 0 ? (
                        <span className="audio-badge">🎵 {freq.audio_files.length}</span>
                      ) : (
                        <span className="audio-badge-empty">–</span>
                      )}
                    </td>
                    <td className="table-actions">
                      <button
                        onClick={() => handleEdit(freq)}
                        className="btn-edit"
                        data-testid={`edit-button-${freq.id}`}
                      >
                        Bearbeiten
                      </button>
                      {deleteConfirm === freq.id ? (
                        <div className="delete-confirm">
                          <button
                            onClick={() => handleDelete(freq.id)}
                            className="btn-delete-confirm"
                            data-testid={`delete-confirm-${freq.id}`}
                          >
                            Bestätigen
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(null)}
                            className="btn-delete-cancel"
                          >
                            Abbrechen
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setDeleteConfirm(freq.id)}
                          className="btn-delete"
                          data-testid={`delete-button-${freq.id}`}
                        >
                          Löschen
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

            {/* Fallback Audio Manager */}
            <div style={{ marginTop: '40px' }}>
              <GeneralAudioManager />
            </div>
          </>
        )}

        {activeTab === 'users' && activeSubTab === 'statistics' && (
          <AdminUserManagement
            users={users}
            onUserCreated={(user) => setUsers([user, ...users])}
            onLoginAsUser={handleLoginAsUser}
            onRefresh={loadData}
          />
        )}

        {activeTab === 'users' && activeSubTab === 'onetime' && <OnetimeProductsAdmin />}

        {activeTab === 'users' && activeSubTab === 'coins' && <CoinsManagement />}

        {activeTab === 'users' && activeSubTab === 'credit-history' && <AdminCreditHistory />}

        {activeTab === 'users' && activeSubTab === 'statistics' && stats && (
          <div className="admin-stats">
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon">👥</div>
                <div className="stat-content">
                  <div className="stat-value">{stats.users.total}</div>
                  <div className="stat-label">Gesamt Users</div>
                  <div className="stat-detail">{stats.users.new_week} diese Woche</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">📊</div>
                <div className="stat-content">
                  <div className="stat-value">{stats.activity.total_activations}</div>
                  <div className="stat-label">Aktivierungen</div>
                  <div className="stat-detail">{stats.activity.activations_week} diese Woche</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">📝</div>
                <div className="stat-content">
                  <div className="stat-value">{stats.activity.total_notes}</div>
                  <div className="stat-label">Journal-Einträge</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">✅</div>
                <div className="stat-content">
                  <div className="stat-value">{stats.users.onboarding_rate}%</div>
                  <div className="stat-label">Onboarding Rate</div>
                </div>
              </div>
            </div>

            <div className="stats-section">
              <h3>🎯 Beliebteste Frequenzen</h3>
              <div className="popular-list">
                {stats.popular_frequencies.map((freq, i) => (
                  <div key={i} className="popular-item">
                    <span className="popular-rank">{i+1}</span>
                    <span className="popular-icon">{freq.frequency_icon}</span>
                    <span className="popular-name">{freq.frequency_name}</span>
                    <span className="popular-count">{freq.count}x</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="stats-section">
              <h3>🏆 Aktivste Users</h3>
              <div className="active-users-list">
                {stats.active_users.map((u, i) => (
                  <div key={i} className="active-user-item">
                    <span className="user-rank">{i+1}</span>
                    <span className="user-name">{u.user_name}</span>
                    <span className="user-email">{u.user_email}</span>
                    <span className="user-count">{u.activation_count} Aktivierungen</span>
                  </div>
                ))}
              </div>
            </div>

            {stats.heatmap && (
              <div className="stats-section">
                <h3>🗓️ Nutzungs-Heatmap (Wochentag × Uhrzeit)</h3>
                <div className="heatmap-container">
                  <div className="heatmap-grid">
                    {['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'].map((day, dayIndex) => (
                      <div key={dayIndex} className="heatmap-row">
                        <div className="heatmap-day-label">{day}</div>
                        {[...Array(24)].map((_, hour) => {
                          const key = `${dayIndex + 1}_${hour}`;
                          const count = stats.heatmap[key] || 0;
                          const maxCount = Math.max(...Object.values(stats.heatmap));
                          const intensity = maxCount > 0 ? count / maxCount : 0;
                          
                          return (
                            <div
                              key={hour}
                              className="heatmap-cell"
                              style={{
                                backgroundColor: intensity > 0 
                                  ? `rgba(194, 164, 90, ${0.2 + intensity * 0.8})`
                                  : 'rgba(255, 255, 255, 0.05)'
                              }}
                              title={`${day} ${hour}:00 - ${count} Aktivierungen`}
                            >
                              {count > 0 && count}
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                  <div className="heatmap-hours">
                    {[0, 6, 12, 18, 24].map(h => (
                      <div key={h} className="hour-label">{h}:00</div>
                    ))}
                  </div>
                </div>
                <p className="chart-insight">
                  💡 Die Heatmap zeigt, wann deine User am aktivsten sind
                </p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'frequencies' && (
          <GeneralAudioManager />
        )}

        {activeTab === 'recommendations' && (
          <RecommendationsAdmin 
            recommendations={recommendations}
            onRefresh={loadData}
          />
        )}

        {activeTab === 'chatbot' && (
          <ChatbotSettings />
        )}

        {activeTab === 'users' && activeSubTab === 'subscription' && subscriptionSettings && (
          <div className="subscription-admin">
            <div className="settings-section">
              <h3>⚙️ Subscription-Einstellungen</h3>
              <div className="settings-form">
                <div className="setting-row">
                  <label>Testphase (Tage):</label>
                  <input
                    type="number"
                    value={subscriptionSettings.trial_days}
                    onChange={async (e) => {
                      const days = parseInt(e.target.value);
                      await apiClient.updateSubscriptionSettings({ trial_days: days });
                      loadData();
                    }}
                    min="0"
                    max="90"
                  />
                </div>
                <div className="setting-row">
                  <label>Monatspreis (€):</label>
                  <input
                    type="number"
                    value={subscriptionSettings.monthly_price}
                    onChange={async (e) => {
                      const price = parseFloat(e.target.value);
                      await apiClient.updateSubscriptionSettings({ monthly_price: price });
                      loadData();
                    }}
                    step="0.01"
                  />
                </div>
                <div className="setting-row">
                  <label>Jahrespreis (€):</label>
                  <input
                    type="number"
                    value={subscriptionSettings.annual_price}
                    onChange={async (e) => {
                      const price = parseFloat(e.target.value);
                      await apiClient.updateSubscriptionSettings({ annual_price: price });
                      loadData();
                    }}
                    step="0.01"
                  />
                </div>
                <div className="setting-row premium-row">
                  <label>Premium Monatspreis (€):</label>
                  <input
                    type="number"
                    value={subscriptionSettings.premium_monthly_price || 40}
                    onChange={async (e) => {
                      const price = parseFloat(e.target.value);
                      await apiClient.updateSubscriptionSettings({ premium_monthly_price: price });
                      loadData();
                    }}
                    step="0.01"
                  />
                </div>
                <div className="setting-row premium-row">
                  <label>Premium Jahrespreis (€):</label>
                  <input
                    type="number"
                    value={subscriptionSettings.premium_annual_price || 399}
                    onChange={async (e) => {
                      const price = parseFloat(e.target.value);
                      await apiClient.updateSubscriptionSettings({ premium_annual_price: price });
                      loadData();
                    }}
                    step="0.01"
                  />
                </div>
              </div>
            </div>

            <div className="transactions-section">
              <h3>💳 Alle Transaktionen</h3>
              {transactions.length === 0 ? (
                <p className="empty-message">Noch keine Transaktionen</p>
              ) : (
                <div className="transactions-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Datum</th>
                        <th>User</th>
                        <th>Plan</th>
                        <th>Betrag</th>
                        <th>Status</th>
                        <th>Session ID</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((t) => (
                        <tr key={t.id}>
                          <td>{new Date(t.created_at).toLocaleString('de-DE')}</td>
                          <td>{t.user_email}</td>
                          <td><span className="plan-badge">{t.plan}</span></td>
                          <td>{t.amount} {t.currency.toUpperCase()}</td>
                          <td>
                            <span className={`status-badge ${t.payment_status}`}>
                              {t.payment_status === 'paid' ? '✅ Bezahlt' :
                               t.payment_status === 'pending' ? '⏳ Ausstehend' :
                               '❌ Fehlgeschlagen'}
                            </span>
                          </td>
                          <td className="session-id">{t.session_id.substring(0, 20)}...</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}\n      </main>

      {showModal && (
        <FrequencyModal
          frequency={editingFrequency}
          onSave={handleSave}
          onClose={() => {
            setShowModal(false);
            setEditingFrequency(null);
          }}
        />
      )}
      </div>
    </>
  );
}

export default AdminDashboard;
